import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import Tooltip from "components/Tooltip/Tooltip";
import "./TableTrashToggle.scss";

type TableTrashToggleProps = {
    /** Whether records that are in the trash are currently shown. */
    showTrash: boolean;
    /** Flip trash visibility. Receives the next value. */
    onToggle: (next: boolean) => void;
};

/**
 * One-click list-controls toggle for whether records that are in the trash are
 * shown in the list. It lives in the RecordTable toolbar's right cluster
 * (alongside the density and column controls) via `customRightToolbarChildren`.
 *
 * It replaces the two-switch "show in trash / show non-trashed" dropdown on
 * resource lists: normal (non-trashed) rows are always shown, and a single
 * click flips ONLY whether trashed rows are additionally included. The richer
 * trash/visibility choice (e.g. hiding non-trashed rows) stays in the advanced
 * search, where that level of detail belongs.
 *
 * Affordance follows `ui-design-principle-affordance-interactivity`: an
 * interactive control uses the icon-button affordance (matching
 * {@link TableDensitySelector}), not display text or an ad-hoc dropdown button.
 * The pressed (trash-shown) state is indicated with the `primary-blue` /
 * `light-blue` semantic tokens (`ui-design-principle-color-palette`), so there
 * is no ad-hoc hex.
 */
const TableTrashToggle = ({ showTrash, onToggle }: TableTrashToggleProps) => (
    <Tooltip overlay={showTrash ? "Hide records in trash" : "Show records in trash"}>
        <span className={`tableTrashToggle${showTrash ? " tableTrashToggle--active" : ""}`}>
            <IconButton
                icon={IconCollection.deleteOutline}
                onClick={() => onToggle(!showTrash)}
            />
        </span>
    </Tooltip>
);

export default TableTrashToggle;
